import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, Play, Sparkles, TrendingDown, TrendingUp, Dumbbell, Zap, Wand2, Mail, X, Salad } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCENARIO_META = {
  deficit: {
    icon: TrendingDown,
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    label: 'Kalorien-Defizit',
    description: 'Kunde hat deutlich weniger gegessen als sein Ziel – Abend-Erinnerung für proteinreiches Essen.',
    vars: ['{name}', '{deficit}'],
    thresholdKey: 'threshold_kcal',
    thresholdLabel: 'Min. Defizit (kcal)',
    thresholdUnit: 'kcal',
  },
  surplus: {
    icon: TrendingUp,
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    label: 'Kalorien-Überschuss',
    description: 'Kunde liegt am Nachmittag schon über dem Ziel – leichte Abendessen-Empfehlung.',
    vars: ['{name}'],
    thresholdKey: 'threshold_pct',
    thresholdLabel: 'Min. Überschuss (%)',
    thresholdUnit: '%',
  },
  protein_gap: {
    icon: Dumbbell,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    label: 'Protein-Lücke',
    description: 'Protein unter X% des Tagesziels am Abend – Empfehlung für Eiweissquellen.',
    vars: ['{name}', '{remaining_protein}'],
    thresholdKey: 'threshold_pct',
    thresholdLabel: 'Schwelle (% vom Ziel)',
    thresholdUnit: '%',
  },
  activity_bonus: {
    icon: Zap,
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    label: 'Aktivitäts-Bonus',
    description: 'Positive Rückmeldung wenn Apple Watch genug Kalorien verbrannt hat.',
    vars: ['{name}', '{bonus}'],
    thresholdKey: 'threshold_kcal',
    thresholdLabel: 'Min. verbrannte kcal',
    thresholdUnit: 'kcal',
  },
  tracking_reminder: {
    icon: Salad,
    color: 'bg-green-50 text-green-700 border-green-200',
    label: 'Tracking-Motivation',
    description: 'Sanfter Wochen-Reminder für Kunden, die früher getrackt haben, aber pausieren. Max. 1x pro 7 Tage.',
    vars: ['{name}'],
    thresholdKey: 'min_inactive_days',
    thresholdLabel: 'Inaktiv seit (Tage)',
    thresholdUnit: 'Tage',
  },
};

export const SmartCoachSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [digest, setDigest] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(null); // scenario key when generating
  const [suggestions, setSuggestions] = useState(null); // { scenario, items }

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [cfgRes, statsRes, recentRes, digestRes] = await Promise.all([
        axios.get(`${API}/admin/smart-coach/config`),
        axios.get(`${API}/admin/smart-coach/stats?days=7`),
        axios.get(`${API}/admin/smart-coach/recent?limit=20`),
        axios.get(`${API}/admin/smart-coach/weekly-digest`),
      ]);
      setConfig(cfgRes.data);
      setStats(statsRes.data);
      setRecent(recentRes.data);
      setDigest(digestRes.data);
    } catch (e) {
      toast.error('Smart Coach Konfiguration konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateScenario = (key, field, value) => {
    setConfig((prev) => ({
      ...prev,
      scenarios: {
        ...prev.scenarios,
        [key]: { ...prev.scenarios[key], [field]: value },
      },
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/smart-coach/config`, {
        enabled: config.enabled,
        max_pushes_per_day: config.max_pushes_per_day ?? 2,
        scenarios: config.scenarios,
      });
      toast.success('Smart Coach gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const triggerNow = async () => {
    try {
      await axios.post(`${API}/admin/smart-coach/trigger-now`);
      toast.success('Smart Coach Tick ausgelöst – prüft gerade alle Kunden');
      setTimeout(loadAll, 2000);
    } catch (e) {
      toast.error('Trigger fehlgeschlagen');
    }
  };
  
  const sendDigestNow = async () => {
    try {
      await axios.post(`${API}/admin/smart-coach/send-weekly-digest`);
      toast.success('Weekly Digest an Admin-E-Mail gesendet');
    } catch (e) {
      toast.error('Digest-Versand fehlgeschlagen');
    }
  };
  
  const generateSuggestions = async (scenarioKey) => {
    setSuggestLoading(scenarioKey);
    setSuggestions(null);
    try {
      const res = await axios.post(`${API}/admin/smart-coach/suggest-templates/${scenarioKey}?count=5`);
      setSuggestions({ scenario: scenarioKey, items: res.data.suggestions || [] });
      toast.success('KI-Vorschläge generiert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Vorschlag fehlgeschlagen');
    } finally {
      setSuggestLoading(null);
    }
  };
  
  const applySuggestion = (scenarioKey, item) => {
    updateScenario(scenarioKey, 'title', item.title);
    updateScenario(scenarioKey, 'template', item.template);
    toast.success('Vorschlag übernommen — vergiss nicht zu speichern');
    setSuggestions(null);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Lade Smart Coach…</div>;
  if (!config) return null;

  return (
    <div className="space-y-6" data-testid="smart-coach-settings">
      {/* Master toggle + stats */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Smart Coach
              </CardTitle>
              <CardDescription>
                Automatische, kontextbezogene Push-Nachrichten basierend auf Ernährung + Apple Watch Aktivität.
              </CardDescription>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
              data-testid="smart-coach-master-toggle"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-sm">
            <div className="col-span-1">
              <div className="text-gray-500">Letzte 7 Tage gesendet</div>
              <div className="text-2xl font-semibold">{stats?.total_sent ?? 0}</div>
            </div>
            {Object.entries(SCENARIO_META).map(([key, meta]) => (
              <div key={key} className="col-span-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <meta.icon className="w-3 h-3" />
                  {meta.label}
                </div>
                <div className="text-2xl font-semibold">{stats?.by_scenario?.[key] ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-end gap-4 flex-wrap">
            <div className="w-48">
              <Label className="text-xs text-gray-500">Max. Pushes pro Kunde/Tag (Anti-Nerv-Limit)</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={config.max_pushes_per_day ?? 2}
                onChange={(e) => setConfig({ ...config, max_pushes_per_day: parseInt(e.target.value) || 2 })}
                data-testid="smart-coach-max-per-day"
              />
            </div>
            <Button variant="outline" size="sm" onClick={triggerNow} data-testid="smart-coach-trigger-now">
              <Play className="w-4 h-4 mr-2" /> Jetzt prüfen (Test)
            </Button>
            <Button variant="outline" size="sm" onClick={sendDigestNow} data-testid="smart-coach-send-digest">
              <Mail className="w-4 h-4 mr-2" /> Weekly Digest jetzt senden
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="smart-coach-save">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichere…' : 'Alle Änderungen speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Weekly Digest Preview Panel */}
      {digest && (
        <Card className="border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="w-5 h-5 text-purple-600" />
              Weekly Digest Vorschau (sonntags 08:00 automatisch)
            </CardTitle>
            <CardDescription>
              Admin bekommt jeden Sonntag diese Zusammenfassung per E-Mail.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-xs text-gray-500">Pushes diese Woche</div>
                <div className="text-xl font-bold mt-1">{digest.total_sent}</div>
                <div className="text-xs mt-1">
                  {digest.trend_pct > 0 && <span className="text-green-600">▲ +{digest.trend_pct}%</span>}
                  {digest.trend_pct < 0 && <span className="text-red-600">▼ {digest.trend_pct}%</span>}
                  {digest.trend_pct === 0 && <span className="text-gray-500">↔ 0%</span>}
                  {' vs. Vorwoche'}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-xs text-gray-500">Kunden erreicht</div>
                <div className="text-xl font-bold mt-1">{digest.unique_customers_reached}</div>
                <div className="text-xs text-gray-500 mt-1">von {digest.total_push_enabled_customers}</div>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <div className="text-xs text-amber-700">Opt-Out-Rate</div>
                <div className="text-xl font-bold text-amber-800 mt-1">{digest.opt_out_rate_pct}%</div>
                <div className="text-xs text-amber-700 mt-1">{digest.opted_out_count} Kunden</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-xs text-gray-500">Vorwoche total</div>
                <div className="text-xl font-bold mt-1">{digest.total_last_week}</div>
                <div className="text-xs text-gray-500 mt-1">zum Vergleich</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scenario cards */}
      {Object.entries(SCENARIO_META).map(([key, meta]) => {
        const scenario = config.scenarios[key] || {};
        const Icon = meta.icon;
        return (
          <Card key={key} className={!config.enabled ? 'opacity-50' : ''}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <Badge variant="outline" className={meta.color}>
                    <Icon className="w-4 h-4 mr-1" /> {meta.label}
                  </Badge>
                </div>
                <Switch
                  checked={scenario.enabled ?? false}
                  onCheckedChange={(v) => updateScenario(key, 'enabled', v)}
                  disabled={!config.enabled}
                  data-testid={`scenario-toggle-${key}`}
                />
              </div>
              <CardDescription>{meta.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Trigger-Uhrzeit (Lokalzeit)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={scenario.trigger_hour ?? 18}
                    onChange={(e) => updateScenario(key, 'trigger_hour', parseInt(e.target.value) || 0)}
                    data-testid={`scenario-hour-${key}`}
                  />
                </div>
                <div>
                  <Label>{meta.thresholdLabel}</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="number"
                      value={scenario[meta.thresholdKey] ?? 0}
                      onChange={(e) =>
                        updateScenario(key, meta.thresholdKey, parseInt(e.target.value) || 0)
                      }
                      data-testid={`scenario-threshold-${key}`}
                    />
                    <span className="text-gray-500 text-sm">{meta.thresholdUnit}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label>Push-Titel</Label>
                <Input
                  value={scenario.title ?? ''}
                  onChange={(e) => updateScenario(key, 'title', e.target.value)}
                  data-testid={`scenario-title-${key}`}
                />
              </div>
              <div>
                <Label>Nachricht</Label>
                <Textarea
                  rows={2}
                  value={scenario.template ?? ''}
                  onChange={(e) => updateScenario(key, 'template', e.target.value)}
                  data-testid={`scenario-template-${key}`}
                />
                <div className="flex items-center justify-between mt-1">
                  <div className="text-xs text-gray-500">
                    Variablen: {meta.vars.join(', ')}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-purple-600 hover:text-purple-800"
                    onClick={() => generateSuggestions(key)}
                    disabled={suggestLoading === key}
                    data-testid={`scenario-ai-suggest-${key}`}
                  >
                    <Wand2 className="w-3 h-3 mr-1" />
                    {suggestLoading === key ? 'KI denkt…' : 'KI-Vorschläge'}
                  </Button>
                </div>
              </div>
              
              {/* AI Suggestions Dropdown */}
              {suggestions?.scenario === key && (
                <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50" data-testid={`ai-suggestions-${key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-semibold text-sm text-purple-900">
                      ✨ KI-Vorschläge (Claude Sonnet 4.5)
                    </div>
                    <button
                      type="button"
                      onClick={() => setSuggestions(null)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {suggestions.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-lg border border-purple-200 hover:border-purple-400 cursor-pointer transition"
                        onClick={() => applySuggestion(key, item)}
                        data-testid={`ai-suggestion-${key}-${idx}`}
                      >
                        <div className="font-semibold text-sm">{item.title}</div>
                        <div className="text-xs text-gray-700 mt-1">{item.template}</div>
                        <div className="text-xs text-purple-600 mt-2">→ Klick zum Übernehmen</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Recent pushes audit log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Zuletzt gesendet</CardTitle>
          <CardDescription>Die letzten 20 Smart-Coach Pushes für Audit/Debugging</CardDescription>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="text-gray-500 text-sm">Noch keine Pushes gesendet.</div>
          ) : (
            <div className="space-y-2">
              {recent.map((entry, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 py-2 border-b last:border-0"
                  data-testid={`recent-push-${idx}`}
                >
                  <Badge variant="outline" className={SCENARIO_META[entry.scenario]?.color}>
                    {SCENARIO_META[entry.scenario]?.label ?? entry.scenario}
                  </Badge>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{entry.title}</div>
                    <div className="text-xs text-gray-600">{entry.body}</div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(entry.sent_at).toLocaleString('de-CH', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
